import Vue from 'vue'
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {IShortUser} from "@/model/shortInterfaces";
import responseHandler from "@/plugins/errorHandling";

interface IUserPermission {
  user_id: number;
  granted_by: number;
  granted_by_name?: string;
  target_id?: number;
  target_name?: string;
  target_deleted_at?: string;
  created_at: string;
}
export interface IUserPermissionLong extends IUserPermission {
  user?: IShortUser;
  target?: object;
}
interface IPermissionsUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface UserPermissionState {
  whoHas: IUserPermission[]|IUserPermissionLong[],
  permissions: IUserPermission[]|IUserPermissionLong[];
  permissionsUser: IPermissionsUser|null;
  errors: IErrors;
}

const state: UserPermissionState = {
  whoHas: [],
  permissions: [],
  permissionsUser: null,
  errors: {},
};

const getters: GetterTree<UserPermissionState, RootState> = {
  whoHas: (state): IUserPermission[]|IUserPermissionLong[] => state.whoHas,
  permissions: (state): IUserPermission[]|IUserPermissionLong[] => state.permissions,
  permissionsUser: (state): IPermissionsUser|null => state.permissionsUser,
  errors: (state) => state.errors,
};

const actions: ActionTree<UserPermissionState, RootState> = {
  clearWhoHas: ({commit}): any => commit('clearWhoHas'),
  fetchWhoHas: ({commit}, filters: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'permissions/whoHas/' + filters.permissionId,
      method: "GET",
      params: filters,
    }),
    commit,
    'whoHasLoaded',
  ),
  fetchPermissions: ({ commit }, userId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/permissions',
    }),
    commit,
    'permissionsLoaded'
  ),
  fetchPermissionsUser: ({ commit }, userId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/permissions/user',
    }),
    commit,
    'permissionsUserLoaded'
  ),
  createPermission: ({ commit }, {userId, permission, target_id}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/permissions',
      method: 'POST',
      data: {permission, target_id}
    }),
    commit,
    'permissionsLoaded'
  ),
  removePermission: ({ commit }, {userId, permission, target_id}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/permissions',
      method: 'DELETE',
      data: {permission, target_id}
    }),
    commit,
    'permissionsLoaded'
  ),
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
  cleanError: ({commit}, index: any): any => {
    commit('cleanError', index);
  },
  cleanErrors: ({commit}): any => {
    commit('cleanErrors');
  }
};

const mutations: MutationTree<UserPermissionState> = {
  clearWhoHas(state) {
    state.whoHas = [];
  },
  whoHasLoaded(state, payload: IUserPermissionLong[]) {
    state.whoHas = state.whoHas.concat(payload);
  },
  permissionsLoaded(state, payload) {
    state.permissions = payload;
  },
  permissionsUserRequest(state) {
    state.permissionsUser = null;
  },
  permissionsUserLoaded(state, payload) {
    state.permissionsUser = payload;
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },
  cleanErrors(state) {
    state.errors = {};
  }
};

const namespaced: boolean = true;

export const userPermissionModule: Module<UserPermissionState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
